import { ActionType, TriggerKey } from './userCommunicationAgent';

export enum ExecutionStatus {
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  PARTIAL_FAILURE = 'PARTIAL_FAILURE',
  FAILURE = 'FAILURE',
  TIMEOUT = 'TIMEOUT',
  WAITING_FOR_REPLY = 'WAITING_FOR_REPLY',
  REJECTED = 'REJECTED',
}

export enum StepExecutionStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  SKIPPED = 'SKIPPED',
  TIMEOUT = 'TIMEOUT',
}

export interface ConditionEvaluation {
  variable: string;
  operator: string;
  value: string;
  resolvedValue: string;
  result: boolean;
}

export interface ConditionResult {
  conditionsMet: boolean;
  logic: 'AND' | 'OR';
  evaluations: ConditionEvaluation[];
  branchExecuted: boolean;
}

export interface ExecutionStepLog {
  order: number;
  stepType: ActionType;
  status: StepExecutionStatus;
  durationMs: number;
  resolvedInput?: string;
  output?: string;
  conditionResult?: ConditionResult;
  /**
   * Variable references this step used that matched no key at all. A variable
   * seeded with a genuinely empty value is absent from this list — the point is
   * to separate "no data" from "this reference never pointed anywhere".
   */
  unresolvedVariables?: string[];
  /** Recipients the send policy removed from this step. A count, never addresses. */
  refusedRecipientCount?: number;
  childStepLogs?: ExecutionStepLog[];
  error?: string;
  retryCount?: number;
}

export interface ExecutionTriggerInfo {
  triggerKey: TriggerKey;
  triggerValues?: string[];
}

export enum PendingQuestionStatus {
  PENDING = 'PENDING',
  ANSWERED = 'ANSWERED',
  EXPIRED = 'EXPIRED',
}

export interface PendingQuestion {
  agentId: string;
  pendingId: string;
  executionId: string;
  timestampId: string;
  userId: string;
  workspaceId: string;
  channel: string;
  threadTs: string;
  pendingThreadKey: string;
  question: string;
  recipient?: string;
  status: PendingQuestionStatus;
  replyText?: string;
  replyAuthor?: string;
  replyTimestamp?: string;
  metadata?: Record<string, string>;
  createdAt: number;
  ttl: number;
}

export interface ConversationState {
  messageHistory: unknown[];
  stepContextEntries: [string, string][];
  stepConfig: Record<string, unknown>;
  agentId: string;
  timestampId: string;
  stepOrder: number;
  pendingQuestionIds: string[];
  waitStrategy: 'all' | 'any';
  timeoutMinutes: number;
  communicationSnapshot?: Record<string, unknown>;
  /**
   * The FlowExecution paused by this wait (top-level AGENT_TASK step only —
   * `stepOrder` is used as-is to key `step.step-{stepOrder}.output` on resume,
   * which is only correct for a top-level step). Optional: absent on
   * ConversationState persisted before this field was added, and on
   * non-Flow-engine agent runs; handleConversationReply.ts logs a warning and
   * skips flow continuation when absent rather than failing.
   */
  flowExecutionId?: string;
}

export interface AgentExecutionHistoryRecord {
  agentId: string;
  timestampId: string;
  executionId: string;
  userId: string;
  messageId?: string;
  communicationSubject?: string;
  correlationId: string;
  status: ExecutionStatus;
  triggerInfo?: ExecutionTriggerInfo;
  steps: ExecutionStepLog[];
  totalDurationMs: number;
  startedAt: number;
  completedAt?: number;
  errorMessage?: string;
  /** Plain-language recap of how the run went, written at finalize time. */
  summary?: string;
  ttl: number;
  createdAt: number;
  conversationState?: ConversationState;
}

export interface PaginatedAgentExecutionHistoryResponse {
  executions: AgentExecutionHistoryRecord[];
  exclusiveStartKey: string | null;
}
